import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiRequest } from '@/lib/supabase'

interface Rule {
  id: string
  name: string
  trigger_type: string
  keyword: string
  is_active: boolean
  trigger_count: number
  connection: { ig_username: string } | null
  created_at: string
}

interface Props {
  rules: Rule[]
  isLoading: boolean
  isConfigured: boolean
}

const triggerLabels: Record<string, string> = {
  exact: 'Exact Match',
  starts_with: 'Starts With',
  contains: 'Contains',
  regex: 'Regex',
}

export function RulesList({ rules, isLoading, isConfigured }: Props) {
  const queryClient = useQueryClient()

  const toggleMutation = useMutation({
    mutationFn: (id: string) => apiRequest(`/api/instagram/rules/${id}/toggle`, 'POST'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ig-rules'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest(`/api/instagram/rules/${id}`, 'DELETE'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ig-rules'] }),
  })

  if (!isConfigured) {
    return (
      <div className="p-5 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40 rounded-xl">
        <div className="flex gap-3">
          <span className="material-symbols-outlined text-amber-500 text-xl mt-0.5">settings</span>
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Instagram integration not set up yet
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400/70 mt-1 leading-relaxed">
              Automation rules require a connected Instagram account. Complete the Meta Developer App setup first to enable this feature.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-16 bg-gray-100 dark:bg-white/5 rounded-lg animate-pulse" />
        ))}
      </div>
    )
  }

  if (rules.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 dark:bg-[#0d121b] rounded-lg">
        <span className="material-symbols-outlined text-4xl text-gray-300 dark:text-gray-600 block mb-2">rule</span>
        <p className="text-sm text-gray-500 dark:text-gray-400">No automation rules yet</p>
        <Link
          to="/instagram/rules/new"
          className="inline-flex items-center gap-1 mt-3 text-sm text-[#1754cf] hover:underline"
        >
          <span className="material-symbols-outlined text-[16px]">add</span>
          Create your first rule
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Rules ({rules.length})</h3>
        <Link
          to="/instagram/rules/new"
          className="text-sm text-[#1754cf] hover:underline flex items-center gap-1"
        >
          <span className="material-symbols-outlined text-[16px]">add</span>
          Create Rule
        </Link>
      </div>

      {rules.map(rule => (
        <div
          key={rule.id}
          className="flex items-center gap-3 p-4 bg-white dark:bg-[#1c2230] border border-gray-200 dark:border-white/10 rounded-lg"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Link
                to={`/instagram/rules/${rule.id}`}
                className="text-sm font-medium text-gray-900 dark:text-white hover:text-[#1754cf] transition-colors"
              >
                {rule.name}
              </Link>
              <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 rounded">
                {triggerLabels[rule.trigger_type] || rule.trigger_type}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
              <span>Keyword: <code className="bg-gray-100 dark:bg-white/5 px-1 rounded">{rule.keyword}</code></span>
              <span>{rule.trigger_count} triggers</span>
              {rule.connection && (
                <span>@{rule.connection.ig_username}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => toggleMutation.mutate(rule.id)}
              className={`p-1 rounded ${rule.is_active ? 'text-green-500' : 'text-gray-300'}`}
              title={rule.is_active ? 'Active' : 'Inactive'}
            >
              <span className="material-symbols-outlined text-[20px]">
                {rule.is_active ? 'toggle_on' : 'toggle_off'}
              </span>
            </button>
            <Link
              to={`/instagram/rules/${rule.id}`}
              className="p-1 text-gray-400 hover:text-[#1754cf]"
            >
              <span className="material-symbols-outlined text-[18px]">edit</span>
            </Link>
            <button
              onClick={() => { if (confirm('Delete this rule?')) deleteMutation.mutate(rule.id) }}
              className="p-1 text-gray-400 hover:text-red-500"
            >
              <span className="material-symbols-outlined text-[18px]">delete</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
